import { useEffect, useRef, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import gsap from 'gsap'
import { useDispatch, useSelector } from 'react-redux'
import { stageType } from '../constants/stageType'
import { setStage, setResult } from 'models/battle'
import { stageSelector, battlePlayersSelector } from 'models/battle/selectors'
import { resultType } from '../constants/resultType'
import { useAuthContext } from '../../../core/providers/AuthProvider'
import { battleService } from '../../../core/services'
import { useSocket } from '../../../core/hooks/useSocket'

import c from './assets/sass/wheel.module.scss'

const buildSlots = (me, opponent, t) => {
    const meSlot = {
        name: t('battle.players.you'),
        avatar: me?.photoUrl || '/images/avatar.png',
    }
    const oppSlot = {
        name: t('battle.players.opponent'),
        avatar: opponent?.photoUrl || '/images/avatar-02.png',
    }
    const arr = []
    for (let i = 0; i < 10; i++) arr.push(meSlot, oppSlot)
    return arr
}

export const Wheel = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { user, updateUserData } = useAuthContext()
    const listRef = useRef(null)
    const itemsRef = useRef([])
    const [slots, setSlots] = useState([])
    const spunRef = useRef(false)
    const pendingSpinRef = useRef(null)
    const { on } = useSocket()
    const stage = useSelector(stageSelector)
    const battlePlayers = useSelector(battlePlayersSelector)
    const userRef = useRef(user)

    // Обновляем ref при изменении user
    useEffect(() => {
        userRef.current = user
    }, [user])

    // Строим слоты когда получаем players из Redux
    useEffect(() => {
        if (battlePlayers && battlePlayers.length > 0) {
            const currentUser = userRef.current
            const me =
                battlePlayers.find(
                    (p) => String(p.user?.id) === String(currentUser?.id)
                )?.user || currentUser
            const opp =
                battlePlayers
                    .map((p) => p.user)
                    .find((u) => String(u?.id) !== String(currentUser?.id)) ||
                null
            const s = buildSlots(me, opp, t)
            setSlots(s)
            console.log('🎰 Slots built from Redux players:', {
                me: me?.username,
                opp: opp?.username,
            })
        }
    }, [battlePlayers])

    // Сбрасываем spunRef при монтировании
    useEffect(() => {
        console.log('🔄 Wheel mounted, resetting spunRef')
        spunRef.current = false
        window.battleSpunRef = false
        pendingSpinRef.current = null
        if (listRef.current) {
            gsap.set(listRef.current, { rotation: 0 })
        }
    }, [])

    const processBattleResultAPI = useCallback(async () => {
        try {
            await battleService.getCurrentBattle()
            await updateUserData()
        } catch (error) {
            console.error('Ошибка обработки результата битвы:', error)
        }
    }, [updateUserData])

    const spinWheel = useCallback(
        (winnerIndex, iWin) => {
            console.log('🎡 spinWheel called:', {
                winnerIndex,
                iWin,
                hasListRef: !!listRef.current,
                spunRef: spunRef.current,
            })

            if (!listRef.current) {
                console.log('⏳ listRef not ready, scheduling retry...')
                pendingSpinRef.current = { winnerIndex, iWin }
                return
            }

            if (spunRef.current) {
                console.log('⚠️ Already spinning/spun, ignoring')
                return
            }

            spunRef.current = true
            window.battleSpunRef = true

            const cellDeg = 18
            const spins = 4
            const targetDeg = 360 - winnerIndex * cellDeg
            const finalRotation = spins * 360 + targetDeg

            console.log('🎡 Starting wheel animation:', {
                cellDeg,
                spins,
                targetDeg,
                finalRotation,
            })

            itemsRef.current.forEach((el) => {
                el?.classList.remove(c.winner)
                el?.classList.remove(c.loser)
            })

            gsap.to(listRef.current, {
                rotation: finalRotation,
                duration: 6,
                ease: 'power2.inOut',
                onStart: () => {
                    console.log('🎡 Animation started')
                },
                onComplete: () => {
                    console.log('🎡 Animation completed')
                    itemsRef.current.forEach((el, i) => {
                        if (i === winnerIndex) {
                            el?.classList.add(c.winner)
                        } else {
                            el?.classList.add(c.loser)
                        }
                    })

                    dispatch(setStage(stageType.finished))
                    dispatch(setResult(iWin ? resultType.win : resultType.lose))

                    processBattleResultAPI()
                },
            })
        },
        [dispatch, processBattleResultAPI]
    )

    // Выполняем отложенный spin когда listRef готов
    useEffect(() => {
        if (listRef.current && pendingSpinRef.current && !spunRef.current) {
            console.log('🎡 Executing pending spin')
            const { winnerIndex, iWin } = pendingSpinRef.current
            pendingSpinRef.current = null
            spinWheel(winnerIndex, iWin)
        }
    })

    // Слушаем только battle_finished (battle_started обрабатывается в Bet)
    useEffect(() => {
        if (!on) {
            console.warn('⚠️ Socket not available')
            return
        }

        console.log('🔌 [Wheel] Registering battle_finished handler')

        const handleBattleFinished = async (payload) => {
            console.log('📨 [Wheel] Received battle_finished event:', payload)
            try {
                if (spunRef.current) {
                    console.log('⚠️ Already spun, just updating data')
                    await updateUserData()
                    return
                }

                const currentUser = userRef.current
                const winnerId = payload?.winnerId
                const player1Id = payload?.player1Id
                const player2Id = payload?.player2Id

                console.log('🎯 Battle data:', {
                    winnerId,
                    player1Id,
                    player2Id,
                })
                console.log('👤 My data:', { id: currentUser?.id })

                const iAmPlayer1 = String(currentUser?.id) === String(player1Id)
                const iWin = String(winnerId) === String(currentUser?.id)

                let winnerIndex
                if (iAmPlayer1) {
                    winnerIndex = String(winnerId) === String(player1Id) ? 0 : 1
                } else {
                    winnerIndex = String(winnerId) === String(player2Id) ? 0 : 1
                }

                console.log(
                    '🏆 I win?',
                    iWin,
                    '🎲 Winner index:',
                    winnerIndex,
                    'Am I player1?',
                    iAmPlayer1
                )

                spinWheel(winnerIndex, iWin)
            } catch (e) {
                console.error('❌ battle_finished handler error:', e)
            }
        }

        const unsub = on('battle_finished', handleBattleFinished)

        return () => {
            console.log('🔌 [Wheel] Unregistering battle_finished handler')
            if (unsub) unsub()
        }
    }, [on, spinWheel, updateUserData])

    // Fallback: если stage=rolling но нет socket событий - загружаем из API
    useEffect(() => {
        if (spunRef.current) return
        if (stage !== stageType.rolling) return

        const run = async () => {
            try {
                console.log('🔍 Fallback: checking battle status from API')
                const res = await battleService.getCurrentBattle()
                const battle = res?.battle
                if (!battle) return

                const players = battle?.players || []
                if (!players.length) return

                const currentUser = userRef.current
                const me =
                    players.find(
                        (p) => String(p.user.id) === String(currentUser?.id)
                    )?.user || currentUser
                const opp =
                    players
                        .map((p) => p.user)
                        .find(
                            (u) => String(u.id) !== String(currentUser?.id)
                        ) || null
                const s = buildSlots(me, opp, t)
                setSlots(s)

                if (battle.status !== 'FINISHED' || !battle.winnerId) {
                    console.log(
                        '⏳ Battle not finished yet, waiting for server result'
                    )
                    return
                }

                const winnerId = battle.winnerId
                const iWin = String(winnerId) === String(currentUser?.id)
                const winnerValue = battle.winnerValue || 50
                const winnerIndex = winnerValue <= 50 ? 0 : 1

                console.log('🎯 Fallback: Server result loaded:', {
                    winnerId,
                    winnerIndex,
                    iWin,
                })

                spinWheel(winnerIndex, iWin)
            } catch (e) {
                console.error('wheel fallback error:', e)
            }
        }

        // Даем время для socket событий, потом проверяем API
        const timer = setTimeout(run, 2000)
        return () => clearTimeout(timer)
    }, [stage, spinWheel])

    return (
        <div
            className={cn(c.component, {
                [c.withPointer]: stage === stageType.rolling,
            })}
        >
            <div className={c.row}>
                <div className={c.wheel}>
                    <ul className={c.list} ref={listRef}>
                        {(slots.length ? slots : buildSlots(user, null, t)).map(
                            (p, i) => (
                                <li
                                    className={c.item}
                                    key={i}
                                    ref={(el) => (itemsRef.current[i] = el)}
                                >
                                    <div className={c.cell}>
                                        <div className={c.avatar}>
                                            <img src={p.avatar} alt={p.name} />
                                        </div>
                                        <span className={c.nickName}>
                                            {p.name}
                                        </span>
                                    </div>
                                </li>
                            )
                        )}
                    </ul>
                </div>
            </div>
        </div>
    )
}
